import React, { Suspense } from 'react';
import { BRAND_PRIMARY } from '../../../utils/colorShades';
import { CHART_COLORS } from '../../../config/statusColors';

const ReactApexChart = React.lazy(() => import('react-apexcharts'));

interface TrendPoint {
  label: string;
  value: number;
}

interface TrendAreaProps {
  series: { name: string; points: TrendPoint[] }[];
  height?: number;
}

const readVar = (name: string, fallback: string) => {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

const Skeleton = ({ height }: { height: number }) => (
  <div className="animate-pulse bg-gray-100 rounded-lg" style={{ height }} aria-hidden="true" />
);

/** Smooth gradient area trend — created (brand) vs completed (green). */
const TrendArea: React.FC<TrendAreaProps> = ({ series, height = 240 }) => {
  const labels = series[0]?.points.map(p => p.label) || [];
  if (labels.length === 0) return null;

  const palette = [readVar('--primary', BRAND_PRIMARY), CHART_COLORS.completed];

  const options: any = {
    chart: {
      type: 'area',
      fontFamily: 'inherit',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { speed: 500 },
    },
    colors: palette,
    stroke: { curve: 'smooth', width: 2.5 },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.02, stops: [0, 95] },
    },
    dataLabels: { enabled: false },
    markers: { size: labels.length === 1 ? 5 : 0, hover: { size: 5 } },
    legend: {
      show: series.length > 1,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '13px',
      markers: { size: 6 },
      labels: { colors: '#404040' },
    },
    grid: { borderColor: 'rgba(0,0,0,0.06)', strokeDashArray: 4 },
    xaxis: {
      categories: labels,
      labels: { style: { colors: '#737373', fontSize: '11px' }, rotate: 0 },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: { style: { colors: '#737373', fontSize: '11px' }, formatter: (v: number) => String(Math.round(v)) },
      forceNiceScale: true,
    },
    tooltip: { shared: true },
  };

  const apexSeries = series.slice(0, 2).map(s => ({ name: s.name, data: s.points.map(p => p.value) }));

  return (
    // Time stays chronological LTR even under RTL.
    <div dir="ltr">
      <Suspense fallback={<Skeleton height={height} />}>
        <ReactApexChart options={options} series={apexSeries} type="area" height={height} />
      </Suspense>
    </div>
  );
};

export default TrendArea;
